import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  STAFFING_STATES,
  STAFFING_STATE_MAP,
  type StaffingState,
} from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { api } from '../../lib/api';
import { num } from '../../lib/format';

interface StaffingRow {
  positionId: string;
  title: string;
  department: string | null;
  gradeName: string | null;
  approved: number;
  filled: number;
  recruiting: number;
  gap: number;
  state: StaffingState;
}

interface Planning {
  rows: StaffingRow[];
  totals: {
    approved: number;
    filled: number;
    recruiting: number;
    short: number;
    hiring: number;
    over: number;
  };
}

/**
 * تخطيط القوى العاملة.
 *
 * المعتمَدُ مقابلَ المشغول — وهو السببُ الأصليُّ لفصل الوظيفة عن الموظّف:
 * بلا وصفٍ وظيفيٍّ له عددٌ معتمَد لا يوجد ما يُقارَن به عددُ الموجودين،
 * فيصير «هل ينقصنا أحد؟» سؤالًا يُجاب بالانطباع.
 *
 * والناقصُ الذي لا يُبحث عنه أوّلًا: «ناقصٌ ويُبحث عنه» عملٌ جارٍ،
 * و«ناقصٌ ولا أحد يبحث» هو ما يُعرض على الإدارة.
 */
export function WorkforcePlanning() {
  const [state, setState] = useState('');

  const plan = useQuery({
    queryKey: ['hr', 'org', 'planning'],
    queryFn: () => api<Planning>('/hr/org/planning'),
  });

  const rows = (plan.data?.rows ?? []).filter(
    (r) => !state || r.state === state,
  );

  /* الناقصُ المهمَل أوّلًا، ثمّ ما يُبحث عنه، ثمّ الباقي. */
  const order: Record<StaffingState, number> = {
    SHORT: 0,
    HIRING: 1,
    OVER: 2,
    FULL: 3,
  };
  const sorted = rows
    .slice()
    .sort((a, b) => order[a.state] - order[b.state] || b.gap - a.gap);

  const t = plan.data?.totals;

  return (
    <div className="page">
      <PageHead
        title="تخطيط القوى العاملة"
        subtitle="العدد المعتمَد في كل وظيفة مقابل من يشغلها فعلًا"
        actions={
          <Link className="btn" to="/hr/org/positions">
            <Icon name="clipboard" size={15} />
            الأوصاف الوظيفية
          </Link>
        }
      />

      {plan.isError && (
        <ErrorState error={plan.error} onRetry={() => void plan.refetch()} />
      )}

      {plan.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {t && (
        <div className="stats">
          <Stat label="المعتمَد" value={num(t.approved)} />
          <Stat
            label="المشغول"
            value={num(t.filled)}
            note={`${num(t.approved - t.filled)} شاغرًا`}
          />
          <Stat
            label="ناقص ولا بحث"
            value={num(t.short)}
            note={t.short > 0 ? 'لم تُنشر لها وظيفة' : undefined}
          />
          <Stat label="قيد التوظيف" value={num(t.hiring)} />
        </div>
      )}

      {/*
        صار للإعلان حقلٌ صريحٌ إلى الوصف المعتمَد، فالمطابقةُ بالنصّ لم تعد
        الطريقَ الأوّل. وبقيت طريقًا ثانيًا لإعلاناتٍ كُتبت قبله ولمن لا
        يربط — وإسقاطُها كان سيُنزل رقمًا يقرؤه الناسُ اليومَ إلى صفر.
      */}
      <Notice title="كيف يُحسب «قيد التوظيف»" icon="info">
        الإعلانُ المنشورُ يُنسب إلى وصفه الوظيفيِّ بالحقل الصريح في نموذج
        الوظيفة. ومن لم يُربط يُطابَق بتشابه المسمّى نصًّا — وذاك تقريبٌ:
        «مصمّم جرافيك أول» و«مصمم جرافيك أوّل» وظيفتان لا واحدة. فاربط
        الإعلانَ بوصفه ليُحسب على وجهه.
      </Notice>

      <div className="toolbar">
        <select
          className="select"
          value={state}
          onChange={(e) => setState(e.target.value)}
        >
          <option value="">كل الحالات</option>
          {STAFFING_STATES.map((s) => (
            <option key={s} value={s}>
              {STAFFING_STATE_MAP[s].label}
            </option>
          ))}
        </select>
      </div>

      {plan.data && plan.data.rows.length === 0 && (
        <EmptyState
          title="لا أوصاف وظيفية بعد"
          text="التخطيطُ مقارنةُ المعتمَد بالمشغول — وبلا وصفٍ وظيفيٍّ له عددٌ معتمَد لا يوجد ما يُقارَن به."
          action={
            <Link className="btn btn-primary" to="/hr/org/positions">
              اكتب أوّل وصف
            </Link>
          }
        />
      )}

      {sorted.length > 0 && (
        <div className="card">
          <div className="table-wrap sticky-head">
            <table className="table">
              <thead>
                <tr>
                  <th>الوظيفة</th>
                  <th>القسم</th>
                  <th>الدرجة</th>
                  <th>المعتمَد</th>
                  <th>المشغول</th>
                  <th>قيد التوظيف</th>
                  <th>الفرق</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const def = STAFFING_STATE_MAP[r.state];
                  return (
                    <tr key={r.positionId}>
                      <td>
                        <Link
                          to={`/hr/org/positions/${r.positionId}`}
                          className="cell-strong link"
                        >
                          {r.title}
                        </Link>
                      </td>
                      <td className="cell-mute">{r.department ?? '—'}</td>
                      <td className="cell-mute">{r.gradeName ?? '—'}</td>
                      <td className="num">{num(r.approved)}</td>
                      <td className="num">{num(r.filled)}</td>
                      <td className="num cell-mute">
                        {r.recruiting > 0 ? num(r.recruiting) : '—'}
                      </td>
                      <td className="num">
                        {r.gap === 0 ? '—' : r.gap > 0 ? `−${r.gap}` : `+${-r.gap}`}
                      </td>
                      <td>
                        <Badge kind={def.kind}>{def.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
